import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import { portalDs } from 'src/components/portal-design-system';
import type { ActivityInsightsUserRow } from '../types/usage-insights.types';
import { formatGenerationsPerDownload, formatMetricValue } from '../utils/usage-insights.utils';

interface UsageInsightsUsersTableProps {
  rows: ActivityInsightsUserRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  from: string;
  to: string;
  workspaceId?: string;
}

export function UsageInsightsUsersTable({
  rows,
  totalCount,
  page,
  pageSize,
  onPageChange,
  from,
  to,
  workspaceId,
}: UsageInsightsUsersTableProps) {
  const columns = useMemo<DataTableColumn<ActivityInsightsUserRow>[]>(() => [
    {
      id: 'user',
      header: 'User',
      accessor: (row) => row.user_email ?? row.user_name ?? row.user_id,
      render: (row) => (
        <Link
          to={buildJourneyPath(row.user_id, { from, to, workspaceId })}
          className="font-medium text-[#2563EB] hover:underline"
        >
          {row.user_email ?? row.user_name ?? row.user_id}
        </Link>
      ),
    },
    {
      id: 'workspace',
      header: 'Workspace',
      accessor: (row) => row.workspace_name ?? row.workspace_id,
    },
    {
      id: 'sessions',
      header: 'Sessions',
      accessor: (row) => row.sessions,
      align: 'right',
      render: (row) => formatMetricValue(row.sessions),
    },
    {
      id: 'generations',
      header: 'Generations',
      accessor: (row) => row.generations,
      align: 'right',
      render: (row) => formatMetricValue(row.generations),
    },
    {
      id: 'downloads',
      header: 'Downloads',
      accessor: (row) => row.downloads,
      align: 'right',
      render: (row) => formatMetricValue(row.downloads),
    },
    {
      id: 'generations_per_download',
      header: 'Gens / download',
      accessor: (row) => row.generations_per_download,
      align: 'right',
      render: (row) => formatGenerationsPerDownload(row.generations_per_download),
    },
    {
      id: 'flag_count',
      header: 'Flags',
      accessor: (row) => row.flag_count,
      align: 'right',
      render: (row) => formatMetricValue(row.flag_count),
    },
  ], [from, to, workspaceId]);

  return (
    <section className="rounded-xl border border-[#EDEEF2] bg-white p-5">
      <h2 className={portalDs.sectionTitle}>Users</h2>
      <div className="mt-4">
        <DataTable
          rows={rows}
          columns={columns}
          getRowKey={(row) => row.user_id}
          emptyMessage="No users match the current filters."
          serverPagination={{
            page,
            pageSize,
            totalCount,
            onPageChange,
          }}
        />
      </div>
    </section>
  );
}

function buildJourneyPath(
  userId: string,
  filters: { from: string; to: string; workspaceId?: string },
): string {
  const params = new URLSearchParams({
    from: filters.from,
    to: filters.to,
  });
  if (filters.workspaceId) {
    params.set('workspace_id', filters.workspaceId);
  }
  return `/operations/usage-insights/users/${userId}?${params.toString()}`;
}
